/**
 * Activity Log Domain
 * What your team has just done, and what rival teams did that every team
 * sees: the lines that rise from the foot of the game screen, beside the rail
 * (common/shell/activity-feed.tsx).
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { getCurrentCohortId } from './current-team';

// ==================== CONFIGURATION ====================

export const ACTIVITY_LOG_CONFIG = {
  /** Lines the feed stands at once; a newer one past them lets the oldest go early. */
  SHOWN_LINES: 4,
  /** Entries kept in memory. */
  MAX_ENTRIES: 50,
  /** A line is gone from the feed by this long after it happened: the feed is for what just happened. */
  SHOWN_FOR_MS: 30_000,
  /** It starts to fade out within its last second there, on the clock's whole seconds; the feed lets it go as its fade ends. */
  FADE_STARTS_WITHIN_MS: 1_000,
};

// ==================== TYPES ====================

/**
 * What can happen. A new action goes in this list and in ACTIVITY_WORDS, and
 * in RIVAL_ACTIVITY_WORDS too if every team sees it, each with its string in
 * every language and the actor in `<name>` (activity-log.test.ts holds the
 * actor to its place).
 */
export const ACTIVITY_LOG_ACTIONS = [
  'flight_added',
  'flight_cancelled',
  'fleet_slot_added',
  'fleet_slot_removed',
  'fleet_slot_updated',
  'airplane_purchased',
  'airplane_sold',
  'city_unlocked',
] as const;

type ActivityLogAction = (typeof ACTIVITY_LOG_ACTIONS)[number];

/** The key of the words that say each action in the player's language, the player who did it in `<name>`. */
export const ACTIVITY_WORDS: Record<ActivityLogAction, string> = {
  flight_added: 'activityLog.flight_added',
  flight_cancelled: 'activityLog.flight_cancelled',
  fleet_slot_added: 'activityLog.fleet_slot_added',
  fleet_slot_removed: 'activityLog.fleet_slot_removed',
  fleet_slot_updated: 'activityLog.fleet_slot_updated',
  airplane_purchased: 'activityLog.airplane_purchased',
  airplane_sold: 'activityLog.airplane_sold',
  city_unlocked: 'activityLog.city_unlocked',
};

/** What every team sees, not only the team that did it: the key of its words when a rival did it, the team in `<name>`. */
export const RIVAL_ACTIVITY_WORDS: Partial<Record<ActivityLogAction, string>> = {
  city_unlocked: 'activityLog.city_unlocked_rival',
};

/** What every team sees, not only the team that did it. */
const CROSS_TEAM_ACTIONS: ReadonlySet<ActivityLogAction> = new Set(
  ACTIVITY_LOG_ACTIONS.filter((action) => RIVAL_ACTIVITY_WORDS[action] !== undefined),
);

export interface ActivityLogEntry {
  /** Unique identifier for this entry */
  id: string;
  /** When it happened, in ms since the epoch */
  timestamp: number;
  action: ActivityLogAction;
  /** The player who did it, when the server says who. */
  userId?: string;
  /** Their team. */
  cohortId: string;
  /** What the action's words need. */
  context: ActivityLogContext;
}

/**
 * What an action's words need. Cities are airport codes, named in the
 * player's language when the line is shown, so a change of language renames
 * them; a model is a configuration's own name.
 */
interface ActivityLogContext {
  origin?: string;
  dest?: string;
  airport?: string;
  model?: string;
  quantity?: number;
}

// ==================== STORE ====================

interface ActivityLogState {
  /** Entries, newest first */
  entries: ActivityLogEntry[];

  /** Add a new entry */
  addEntry: (entry: Omit<ActivityLogEntry, 'id' | 'timestamp'>) => void;

  /** Reset to initial state */
  reset: () => void;
}

const initialState = {
  entries: [],
};

/**
 * Activity Log Store
 * Manages activity log entries for the current match
 */
export const useActivityLogStore = create<ActivityLogState>()(
  devtools(
    (set) => ({
      ...initialState,

      /**
       * Add a new log entry
       * Auto-generates ID and timestamp, and trims to MAX_ENTRIES
       */
      addEntry: (entry) => {
        const id = `activity-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
        const timestamp = Date.now();

        set(
          (state) => ({
            entries: [{ ...entry, id, timestamp }, ...state.entries].slice(0, ACTIVITY_LOG_CONFIG.MAX_ENTRIES),
          }),
          false,
          'addEntry'
        );
      },

      /**
       * Reset the activity log store to initial state
       */
      reset: () => {
        set(initialState, false, 'reset');
      },
    }),
    {
      name: 'activity-log-store',
    }
  )
);

// ==================== SELECTORS ====================

/** Whether a team sees an action: everything its own players do, and what every team sees. */
function seenBy(cohortId: string | undefined, entry: Pick<ActivityLogEntry, 'action' | 'cohortId'>): boolean {
  return !!cohortId && (entry.cohortId === cohortId || CROSS_TEAM_ACTIONS.has(entry.action));
}

/**
 * The entries the feed can show now, newest first: what your team sees, none
 * older than SHOWN_FOR_MS.
 */
export function recentActivity(
  entries: readonly ActivityLogEntry[],
  cohortId: string | undefined,
  now: number,
): ActivityLogEntry[] {
  return entries.filter((entry) => seenBy(cohortId, entry) && now - entry.timestamp < ACTIVITY_LOG_CONFIG.SHOWN_FOR_MS);
}

/** The key of a line's words for a team: what a rival did, for what every team sees, or what one of its own did. */
export function activityWords(cohortId: string | undefined, entry: Pick<ActivityLogEntry, 'action' | 'cohortId'>): string {
  return (entry.cohortId !== cohortId && RIVAL_ACTIVITY_WORDS[entry.action]) || ACTIVITY_WORDS[entry.action];
}

/** Whether a line is in its last second on the feed: fading out, and gone from the feed once its fade has run. */
export function isLeaving(entry: Pick<ActivityLogEntry, 'timestamp'>, now: number): boolean {
  return now - entry.timestamp >= ACTIVITY_LOG_CONFIG.SHOWN_FOR_MS - ACTIVITY_LOG_CONFIG.FADE_STARTS_WITHIN_MS;
}

/** A line the feed draws, and whether it is on its way out. */
interface FeedLine {
  entry: ActivityLogEntry;
  leaving: boolean;
}

/**
 * The lines the feed draws, in the order they stack — oldest first, so the
 * newest stands at the foot — from what it can show now, newest first
 * (recentActivity). A line is leaving in its last second, or once
 * SHOWN_LINES newer ones stand under it: a fifth coming in lets the oldest go
 * early, fading as it goes.
 */
export function feedLines(recent: readonly ActivityLogEntry[], now: number): FeedLine[] {
  return recent.map((entry, newer) => ({ entry, leaving: newer >= ACTIVITY_LOG_CONFIG.SHOWN_LINES || isLeaving(entry, now) })).reverse();
}

// ==================== HELPER FUNCTIONS ====================

/**
 * Add an activity log entry, if your team sees it. The server tells every
 * client what every team does; kept, a rival's own actions would push your
 * team's out of the log.
 */
export function addActivityLogEntry(
  action: ActivityLogAction,
  actor: { userId?: string; cohortId: string },
  context: ActivityLogContext = {}
): void {
  if (!seenBy(getCurrentCohortId(), { action, cohortId: actor.cohortId })) return;
  useActivityLogStore.getState().addEntry({
    action,
    userId: actor.userId,
    cohortId: actor.cohortId,
    context,
  });
}
